// Growlab 811 — kinetic effects: diagonal airflow particles, humidifier vapor,
// UIS data links, and the camera rig that glides to focused devices.
import React, { useMemo, useRef } from "react";
import * as THREE from "three";
import { useFrame } from "@react-three/fiber";
import { Line } from "@react-three/drei";
import { DEVICES } from "./data";

const AIR_POINTS = [
  [3.8, -3.2, 3.8], [2.2, -2.8, 2.2], [0.4, -1.0, 0.6],
  [-1.0, 0.8, -0.6], [-2.6, 2.4, -2.4], [-3.8, 3.5, -3.8],
];
const MAX_PARTICLES = 140;
const C_IN = new THREE.Color("#06b6d4");
const C_MID = new THREE.Color("#10b981");
const C_OUT = new THREE.Color("#f97316");

export function AirflowParticles({ running, dense, speed }) {
  const curve = useMemo(() => new THREE.CatmullRomCurve3(AIR_POINTS.map((p) => new THREE.Vector3(...p))), []);
  const offsets = useMemo(() => Array.from({ length: MAX_PARTICLES }, () => Math.random()), []);
  const geo = useMemo(() => {
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(new Float32Array(MAX_PARTICLES * 3), 3));
    g.setAttribute("color", new THREE.BufferAttribute(new Float32Array(MAX_PARTICLES * 3), 3));
    return g;
  }, []);
  useFrame(({ clock }) => {
    if (!running) return;
    const t0 = clock.getElapsedTime() * 0.055 * speed;
    const pos = geo.attributes.position;
    const col = geo.attributes.color;
    for (let i = 0; i < MAX_PARTICLES; i++) {
      const t = (t0 + offsets[i]) % 1;
      const p = curve.getPoint(t);
      pos.setXYZ(i, p.x, p.y, p.z);
      const c = t < 0.5 ? C_IN.clone().lerp(C_MID, t * 2) : C_MID.clone().lerp(C_OUT, (t - 0.5) * 2);
      col.setXYZ(i, c.r, c.g, c.b);
    }
    pos.needsUpdate = true;
    col.needsUpdate = true;
    geo.setDrawRange(0, dense ? MAX_PARTICLES : 50);
  });
  return (
    <points geometry={geo}>
      <pointsMaterial size={0.1} vertexColors transparent opacity={0.95} sizeAttenuation depthWrite={false} />
    </points>
  );
}

export function VaporPlume({ running, origin = [-3.5, -1.6, -1.0] }) {
  const COUNT = 60;
  const seeds = useMemo(() => Array.from({ length: COUNT }, () => ({ t: Math.random(), a: Math.random() * Math.PI * 2, r: 0.15 + Math.random() * 0.5 })), []);
  const geo = useMemo(() => {
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(new Float32Array(COUNT * 3), 3));
    return g;
  }, []);
  useFrame(({ clock }) => {
    if (!running) return;
    const pos = geo.attributes.position;
    const now = clock.getElapsedTime();
    for (let i = 0; i < COUNT; i++) {
      const s = seeds[i];
      const t = (now * 0.12 + s.t) % 1;
      const drift = Math.sin(now * 0.8 + s.a) * 0.25 * t;
      pos.setXYZ(i, origin[0] + Math.cos(s.a) * s.r * t + drift, origin[1] + t * 5.2, origin[2] + Math.sin(s.a) * s.r * t);
    }
    pos.needsUpdate = true;
  });
  return (
    <points geometry={geo}>
      <pointsMaterial size={0.16} color="#e7fdfa" transparent opacity={0.35} sizeAttenuation depthWrite={false} />
    </points>
  );
}

export function UISLinks({ selected }) {
  const host = DEVICES.find((d) => d.id === "controller").pos;
  const links = DEVICES.filter((d) => !["controller"].includes(d.id)).map((d) => {
    const mid = [(host[0] + d.pos[0]) / 2, Math.max(host[1], d.pos[1]) + 1.2, (host[2] + d.pos[2]) / 2];
    return { id: d.id, pts: [host, mid, d.pos], color: d.color };
  });
  return (
    <group>
      {links.map((l) => (
        <Line key={l.id} points={l.pts} color={l.color} transparent opacity={selected === l.id ? 0.9 : 0.22} lineWidth={selected === l.id ? 2 : 1} dashed dashScale={6} />
      ))}
    </group>
  );
}

export function CameraRig({ focus, controlsRef }) {
  const target = useMemo(() => new THREE.Vector3(), []);
  const goal = useMemo(() => new THREE.Vector3(), []);
  useFrame(({ camera }) => {
    if (!focus || !controlsRef.current) return;
    goal.set(...focus.pos);
    target.set(...focus.target);
    camera.position.lerp(goal, 0.05);
    controlsRef.current.target.lerp(target, 0.07);
    controlsRef.current.update();
  });
  return null;
}
